import { useState, FormEvent } from 'react';
import { CheckCircle } from 'lucide-react';
import { EnterpriseContactData } from '../../types/plan';
import { useTranslation } from '../../hooks/useTranslation';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

interface EnterpriseContactFormProps {
  onSubmit: (data: EnterpriseContactData) => Promise<void>;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  companyName?: string;
  phoneNumber?: string;
  expectedFeeds?: string;
  expectedMonthlyEvents?: string;
}

export const EnterpriseContactForm = ({ onSubmit }: EnterpriseContactFormProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const [formData, setFormData] = useState<EnterpriseContactData>({
    fullName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    expectedFeeds: 0,
    expectedMonthlyEvents: 0,
    additionalRequirements: '',
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Basic phone validation - at least 10 digits
    const phoneRegex = /^\+?[\d\s\-()]{10,}$/;
    return phoneRegex.test(phone);
  };

  const validateField = (name: keyof EnterpriseContactData, value: any): string | undefined => {
    switch (name) {
      case 'fullName':
        if (!value || value.trim().length === 0) {
          return t('validation.required');
        }
        if (value.trim().length < 3) {
          return t('validation.minLength', { count: 3 });
        }
        break;

      case 'email':
        if (!value || value.trim().length === 0) {
          return t('validation.required');
        }
        if (!validateEmail(value)) {
          return t('validation.email');
        }
        break;

      case 'companyName':
        if (!value || value.trim().length === 0) {
          return t('validation.required');
        }
        break;

      case 'phoneNumber':
        if (!value || value.trim().length === 0) {
          return t('validation.required');
        }
        if (!validatePhone(value)) {
          return 'Invalid phone number format';
        }
        break;

      case 'expectedFeeds':
        if (!value || value <= 0) {
          return 'Please enter a valid number';
        }
        break;

      case 'expectedMonthlyEvents':
        if (!value || value <= 0) {
          return 'Please enter a valid number';
        }
        break;
    }
    return undefined;
  };

  const handleBlur = (name: keyof EnterpriseContactData) => {
    setTouched({ ...touched, [name]: true });
    const error = validateField(name, formData[name]);
    setErrors({ ...errors, [name]: error });
  };

  const handleChange = (
    name: keyof EnterpriseContactData,
    value: string | number
  ) => {
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors({ ...errors, [name]: error });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    (Object.keys(formData) as Array<keyof EnterpriseContactData>).forEach((key) => {
      if (key !== 'additionalRequirements') {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    );
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      setIsSuccess(true);
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        expectedFeeds: 0,
        expectedMonthlyEvents: 0,
        additionalRequirements: '',
      });
      setTouched({});
      setErrors({});
    } catch (error) {
      console.error('Failed to submit enterprise contact form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Card className="max-w-2xl mx-auto">
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {t('plans.enterpriseContact.successTitle')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('plans.enterpriseContact.successMessage')}
          </p>
          <Button
            variant="primary"
            onClick={() => setIsSuccess(false)}
          >
            {t('common.close')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {t('plans.enterpriseContact.contactTitle')}
        </h3>
        <p className="text-gray-600">
          Fill out the form below and our sales team will contact you shortly.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name */}
        <Input
          label={t('plans.enterpriseContact.fullName')}
          type="text"
          value={formData.fullName}
          onChange={(e) => handleChange('fullName', e.target.value)}
          onBlur={() => handleBlur('fullName')}
          error={touched.fullName ? errors.fullName : undefined}
          required
        />

        {/* Email */}
        <Input
          label={t('plans.enterpriseContact.email')}
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          onBlur={() => handleBlur('email')}
          error={touched.email ? errors.email : undefined}
          required
        />

        {/* Company Name */}
        <Input
          label={t('plans.enterpriseContact.company')}
          type="text"
          value={formData.companyName}
          onChange={(e) => handleChange('companyName', e.target.value)}
          onBlur={() => handleBlur('companyName')}
          error={touched.companyName ? errors.companyName : undefined}
          required
        />

        {/* Phone Number */}
        <Input
          label={t('plans.enterpriseContact.phone')}
          type="tel"
          value={formData.phoneNumber}
          onChange={(e) => handleChange('phoneNumber', e.target.value)}
          onBlur={() => handleBlur('phoneNumber')}
          error={touched.phoneNumber ? errors.phoneNumber : undefined}
          placeholder="+1 (555) 123-4567"
          required
        />

        {/* Expected Feeds */}
        <Input
          label={t('plans.enterpriseContact.expectedFeeds')}
          type="number"
          min="1"
          value={formData.expectedFeeds || ''}
          onChange={(e) => handleChange('expectedFeeds', parseInt(e.target.value) || 0)}
          onBlur={() => handleBlur('expectedFeeds')}
          error={touched.expectedFeeds ? errors.expectedFeeds : undefined}
          required
        />

        {/* Expected Monthly Events */}
        <Input
          label={t('plans.enterpriseContact.expectedEvents')}
          type="number"
          min="1"
          value={formData.expectedMonthlyEvents || ''}
          onChange={(e) =>
            handleChange('expectedMonthlyEvents', parseInt(e.target.value) || 0)
          }
          onBlur={() => handleBlur('expectedMonthlyEvents')}
          error={touched.expectedMonthlyEvents ? errors.expectedMonthlyEvents : undefined}
          required
        />

        {/* Additional Requirements */}
        <div>
          <label
            htmlFor="additionalRequirements"
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--wl-secondary)' }}
          >
            {t('plans.enterpriseContact.additionalRequirements')}
          </label>
          <textarea
            id="additionalRequirements"
            rows={4}
            value={formData.additionalRequirements}
            onChange={(e) => handleChange('additionalRequirements', e.target.value)}
            className="block w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[var(--wl-primary)] focus:border-transparent"
            placeholder="Tell us about your specific needs..."
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={isSubmitting}
          >
            {isSubmitting
              ? t('plans.enterpriseContact.submitting')
              : t('plans.enterpriseContact.submit')}
          </Button>
        </div>
      </form>
    </Card>
  );
};
